import { cn } from "web-design-system";
import {
  AllocationBucket,
  PortfolioReview,
  PortfolioReviewReport,
} from "creso-core/types";
import { formatAsCrore, formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import {
  JourneyMetricsTable,
  ReturnsChart,
  SignedPercent,
} from "@/modules/portfolio-review/components/portfolio-journey";
import {
  AssetAllocationComparison,
  StackedBar,
  bucketColor,
} from "@/modules/portfolio-review/components/asset-allocation-comparison";
import { ReportPageTitle, ReportStats, SignedReturn } from "./report-blocks";
import { RankedItem, positionsBefore, topAmcs, topFunds } from "./report-data";

const formatPct = (value: number | string) => formatAsPercentage(value, "-", 0);

export function PerformancePage({ report }: { report: PortfolioReviewReport }) {
  const { overview } = report;
  const { backtest } = overview;
  const invested = Number(overview.amount_invested ?? 0);
  const current = Number(overview.current_value ?? 0);
  const gain = current - invested;
  const { current: currentMetrics, benchmark: benchmarkMetrics } =
    backtest.metrics;
  const returnsOf = (m: NonNullable<typeof currentMetrics>) =>
    formatAsCrore(
      Number(m.final_value) +
        Number(m.total_withdrawn) -
        Number(m.total_invested)
    );

  return (
    <div className="flex flex-col gap-6">
      <ReportStats
        stats={[
          { label: "Current value", value: formatAsCrore(current) },
          { label: "Invested", value: formatAsCrore(invested) },
          {
            label: "Returns",
            value: (
              <span className="flex items-baseline gap-1">
                {formatAsCrore(gain)}
                {invested ? (
                  <span className="text-xs font-normal">
                    <SignedReturn value={(gain / invested) * 100} />
                  </span>
                ) : null}
              </span>
            ),
          },
          {
            label: "XIRR",
            value: overview.xirr ? (
              <SignedReturn value={Number(overview.xirr)} />
            ) : (
              "-"
            ),
          },
        ]}
      />
      <div className="flex flex-col gap-2">
        <ReportPageTitle title="Returns Backtest" />
        <ReturnsChart data={backtest} grid={false} className="h-64" />
      </div>
      {currentMetrics && benchmarkMetrics ? (
        <JourneyMetricsTable
          benchmarkName={backtest.benchmark_name}
          caption="For the same investment"
          markers
          rows={[
            {
              label: "Final value",
              current: formatAsCrore(currentMetrics.final_value),
              benchmark: formatAsCrore(benchmarkMetrics.final_value),
            },
            {
              label: "Returns",
              current: returnsOf(currentMetrics),
              benchmark: returnsOf(benchmarkMetrics),
            },
            {
              label: "XIRR",
              current: <SignedPercent value={currentMetrics.xirr} />,
              benchmark: <SignedPercent value={benchmarkMetrics.xirr} />,
            },
          ]}
        />
      ) : null}
    </div>
  );
}

export function PortfolioOverviewPage({
  review,
  report,
  amcName,
}: {
  review: PortfolioReview;
  report: PortfolioReviewReport;
  amcName: (code: string | null) => string;
}) {
  const { before } = report.summary.exposure;
  const positions = positionsBefore(review.lines);
  const funds = topFunds(positions);
  const buckets = report.overview.asset_allocation.before;

  return (
    <div className="flex flex-col gap-6">
      <ReportPageTitle title="Portfolio Overview" />
      {buckets.length ? (
        <div className="flex flex-col gap-4 rounded-lg bg-card p-4">
          <span className="text-sm font-semibold">Asset Allocation</span>
          <StackedBar buckets={buckets} />
          <AllocationLegend buckets={buckets} />
        </div>
      ) : null}
      <div className="flex flex-col gap-4 rounded-lg bg-card p-4">
        <span className="text-sm font-semibold">
          {before.fund_count} funds from {before.amc_count} AMCs
        </span>
        <TopThreeRow
          label={`Top 3 AMCs: ${formatPct(before.top_3_amc_pct)}`}
          items={topAmcs(positions, amcName)}
        />
        <TopThreeRow
          label={`Top 3 Funds: ${formatPct(sumPct(funds))}`}
          items={funds}
        />
      </div>
    </div>
  );
}

// The first bucket leads on the left with its breakdown, the rest sit right.
function AllocationLegend({ buckets }: { buckets: AllocationBucket[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
      {buckets
        .filter((bucket) => bucket.pct > 0)
        .map((bucket, index) => (
          <span
            key={bucket.key}
            className={cn("flex items-center gap-2", index === 0 && "mr-auto")}
          >
            <span
              className="size-3 shrink-0 rounded-xs"
              style={{ background: bucketColor(bucket.key, index) }}
            />
            <span className="font-semibold">
              {bucket.label}: {formatPct(bucket.pct)}
            </span>
            {bucket.key.toLowerCase() === "indian_equity" &&
            bucket.sub_buckets?.length ? (
              <span className="ml-2 flex gap-3">
                (
                {bucket.sub_buckets.map((sub) => (
                  <span key={sub.key}>
                    {sub.label}: {formatPct(sub.pct)}
                  </span>
                ))}
                )
              </span>
            ) : null}
          </span>
        ))}
    </div>
  );
}

function TopThreeRow({ label, items }: { label: string; items: RankedItem[] }) {
  const max = Math.max(...items.map((item) => item.pct), 1);
  return (
    <div className="flex items-center gap-4 rounded-lg bg-secondary p-2">
      <span className="w-32 shrink-0 pl-2 text-xs font-semibold">{label}</span>
      <div className="grid flex-1 grid-cols-3 gap-2">
        {items.map((item) => (
          <div
            key={item.key}
            className="flex items-center gap-2 rounded-lg bg-background p-2"
          >
            {/* A bar behind the name, sized to the item's share. */}
            <div className="relative flex min-w-0 flex-1 items-center gap-2">
              <div
                className="absolute inset-y-0 left-0 min-w-10 rounded-lg bg-tag-blue"
                style={{ width: `${(item.pct / max) * 80}%` }}
              />
              <span className="relative">
                <AmcLogo amc_code={item.amcCode} />
              </span>
              <span className="relative flex-1 truncate text-xs">
                {item.name}
              </span>
            </div>
            <span className="pr-2 text-sm font-semibold">
              {formatPct(item.pct)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AssetAllocationPage({
  report,
}: {
  report: PortfolioReviewReport;
}) {
  const { before, after } = report.overview.asset_allocation;
  return (
    <div className="flex flex-col gap-6">
      <ReportPageTitle title="Asset allocation" />
      <AssetAllocationComparison before={before} after={after} />
    </div>
  );
}

const sumPct = (items: RankedItem[]) =>
  items.reduce((sum, item) => sum + item.pct, 0);
